import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import toast from 'react-hot-toast'
import SharedPageHeader from '../../components/Shared/SharedPageHeader'
import CustomTabs from '../../components/Shared/CustomTabs'
import CustomTable from '../../components/Shared/CustomTable'
import SharedButton from '../../components/Shared/SharedButton'
import AutoCompleteField from '../../components/Shared/AutoCompleteField'
import DatePicker from '../../components/Shared/DatePicker'
import useFetchData from '../../hooks/useFetchData'
import { getOneMonthAgo } from '../CashInflow/CashInflow'
import { MdOutlineLibraryBooks } from 'react-icons/md'
import TrialBalanceTable from '../TrialBalance/TrialBalanceTable'

const getToday = () =>
  new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .split('T')[0]

const toDateString = date =>
  date
    ? new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString()
      .split('T')[0]
    : ''

const formatAmount = value => Number(value || 0).toFixed(2)

const formatDate = value => {
  if (!value) return ''
  return new Date(value).toLocaleDateString('en-GB')
}

const formatAccountOptions = (data, lang) =>
  data
    ? Object.entries(data).map(([, account]) => ({
      value: String(account?.code || ''),
      label:
        lang === 'ar'
          ? `${account.name_ar} ${account?.code}`
          : `${account.name_en} ${account?.code}`
    }))
    : []

const FilterBar = ({
  accountValue,
  setAccountValue,
  fromAccount,
  setFromAccount,
  toAccount,
  setToAccount,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  costCenterId,
  setCostCenterId,
  onApply,
  onReset,
  loading,
  showRange = false
}) => {
  const { t } = useTranslation()
  const lang = i18next.language
  const fieldClassName = 'w-full sm:w-[210px] lg:w-[230px]'

  return (
    <div className='flex flex-wrap items-start justify-end gap-2 mb-4'>
      {showRange ? (
        <>
          <div className={fieldClassName}>
            <AutoCompleteField
              useFormik={false}
              placeholder={t('From Account')}
              value={fromAccount}
              onChange={e => setFromAccount(e.value)}
              fieldUrl='searchAllAccounts'
              optionsFormat={data => formatAccountOptions(data, lang)}
            />
          </div>
          <div className={fieldClassName}>
            <AutoCompleteField
              useFormik={false}
              placeholder={t('To Account')}
              value={toAccount}
              onChange={e => setToAccount(e.value)}
              fieldUrl='searchAllAccounts'
              optionsFormat={data => formatAccountOptions(data, lang)}
            />
          </div>
        </>
      ) : (
        <div className={fieldClassName}>
          <AutoCompleteField
            useFormik={false}
            placeholder={t('Select Account')}
            value={accountValue}
            onChange={e => setAccountValue(e.value)}
            fieldUrl='searchAllAccounts'
            optionsFormat={data => formatAccountOptions(data, lang)}
          />
        </div>
      )}
      <div className='w-full sm:w-[150px] lg:w-[160px]'>
        <DatePicker
          useFormik={false}
          value={startDate ? new Date(startDate) : null}
          onChange={e => setStartDate(toDateString(e.value))}
        />
      </div>
      <div className='w-full sm:w-[150px] lg:w-[160px]'>
        <DatePicker
          useFormik={false}
          value={endDate ? new Date(endDate) : null}
          onChange={e => setEndDate(toDateString(e.value))}
        />
      </div>
      <div className='w-full sm:w-[150px] lg:w-[160px]'>
        <input
          className='w-full h-[30px] border border-gray-300 rounded-md px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary'
          placeholder={t('costCenter')}
          value={costCenterId}
          onChange={e => setCostCenterId(e.target.value)}
        />
      </div>
      <div className='flex items-center justify-end gap-2'>
        <SharedButton
          title={t('apply_filter')}
          icon='pi pi-filter'
          onClick={onApply}
          loading={loading}
          disabled={loading}
        />
        <SharedButton
          title={t('Reset')}
          buttonType='reset'
          icon='pi pi-sync'
          onClick={onReset}
          disabled={loading}
        />
      </div>
    </div>
  )
}

const JournalLedgerList = () => {
  const { t } = useTranslation()
  const lang = i18next.language
  const today = getToday()
  const defaultStartDate = getOneMonthAgo(today)
  const [account, setAccount] = useState(null)
  const [startDate, setStartDate] = useState(defaultStartDate)
  const [endDate, setEndDate] = useState(today)
  const [costCenterId, setCostCenterId] = useState('')
  const { data: ledgerResponse, loading, executeFetch } = useFetchData()

  const rows = Array.isArray(ledgerResponse)
    ? ledgerResponse
    : ledgerResponse?.data || []

  const fetchLedger = () => {
    executeFetch('POST', 'journal-ledger/list', {
      account_code: account?.value ? String(account.value) : '',
      start_date: startDate || '',
      end_date: endDate || '',
      cost_center_id: costCenterId ? String(costCenterId) : ''
    })
  }

  const reset = () => {
    setAccount(null)
    setStartDate(defaultStartDate)
    setEndDate(today)
    setCostCenterId('')
    executeFetch('POST', 'journal-ledger/list', {
      account_code: '',
      start_date: defaultStartDate,
      end_date: today,
      cost_center_id: ''
    })
  }

  const rebuild = async () => {
    const result = await executeFetch('POST', 'journal-ledger/rebuild', {})
    if (result?.message) toast.success(result.message)
  }

  const columns = useMemo(
    () => [
      {
        header: t('date'),
        body: row => formatDate(row?.date)
      },
      {
        field: 'journal_entry_number',
        header: t('entry_number')
      },
      {
        field: 'account_code',
        header: t('Account Code')
      },
      {
        header: t('AccountName'),
        body: row =>
          lang === 'ar'
            ? row?.account_name_ar || row?.account_name_en || ''
            : row?.account_name_en || row?.account_name_ar || ''
      },
      {
        field: 'description',
        header: t('description')
      },
      {
        field: 'entry_type',
        header: t('type')
      },
      {
        header: t('Debit'),
        body: row => formatAmount(row?.debit)
      },
      {
        header: t('Credit'),
        body: row => formatAmount(row?.credit)
      },
      {
        header: t('Balance'),
        body: row => formatAmount(row?.balance)
      }
    ],
    [lang, t]
  )

  return (
    <>
      <div className='flex flex-col items-start justify-start w-full'>
        <div className='flex justify-end items-center flex-wrap gap-2 mb-2 w-full'>
          {/* <div className="text-sm text-gray-500">{rows.length} {t("records")}</div> */}
          <SharedButton
            title={t('Rebuild Ledger')}
            label={t('Rebuild Ledger')}
            icon='pi pi-refresh'
            onClick={rebuild}
            loading={loading}
            className='!w-auto !h-[25px]'
          />
        </div>
        <FilterBar
          accountValue={account}
          setAccountValue={setAccount}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          costCenterId={costCenterId}
          setCostCenterId={setCostCenterId}
          onApply={fetchLedger}
          onReset={reset}
          loading={loading}
        />
      </div>
      <CustomTable data={rows} columns={columns} isLoading={loading} />
    </>
  )
}

const JournalLedgerTrialBalance = () => {
  const today = getToday()
  const defaultStartDate = getOneMonthAgo(today)
  const [fromAccount, setFromAccount] = useState(null)
  const [toAccount, setToAccount] = useState(null)
  const [startDate, setStartDate] = useState(defaultStartDate)
  const [endDate, setEndDate] = useState(today)
  const [costCenterId, setCostCenterId] = useState('')
  const { data: trialResponse, loading, executeFetch } = useFetchData()

  const rows = Array.isArray(trialResponse)
    ? trialResponse
    : trialResponse?.data || []

  const flattenTrialBalanceTree = (items = [], level = 0) => {
    let result = []

    items.forEach(item => {
      const children = Array.isArray(item?.children) ? item.children : []
      const row = {
        ...item,
        level,
        children
      }

      result.push(row)

      if (children.length > 0) {
        result = result.concat(flattenTrialBalanceTree(children, level + 1))
      }
    })

    return result
  }

  const flatRows = flattenTrialBalanceTree(rows)

  const fetchTrialBalance = () => {
    executeFetch('POST', 'reports/journalLedgerTrialBalance', {
      start_date: startDate || '',
      end_date: endDate || '',
      from_account: fromAccount?.value ? String(fromAccount.value) : '',
      to_account: toAccount?.value ? String(toAccount.value) : '',
      cost_center_id: costCenterId ? String(costCenterId) : ''
    })
  }

  const reset = () => {
    setFromAccount(null)
    setToAccount(null)
    setStartDate(defaultStartDate)
    setEndDate(today)
    setCostCenterId('')
    executeFetch('POST', 'reports/journalLedgerTrialBalance', {
      start_date: defaultStartDate,
      end_date: today,
      from_account: '',
      to_account: '',
      cost_center_id: ''
    })
  }

  return (
    <div>
      <div className='flex justify-start text-sm  text-gray-500'>
        <FilterBar
          fromAccount={fromAccount}
          setFromAccount={setFromAccount}
          toAccount={toAccount}
          setToAccount={setToAccount}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          costCenterId={costCenterId}
          setCostCenterId={setCostCenterId}
          onApply={fetchTrialBalance}
          onReset={reset}
          loading={loading}
          showRange
        />
      </div>
      <TrialBalanceTable data={{ data: flatRows }} isLoading={loading} />
    </div>
  )
}

const JournalLedgerMain = () => {
  const { t } = useTranslation()

  const tabs = [
    {
      name: 'Journal Ledger',
      content: () => <JournalLedgerList />
    },
    {
      name: 'Trial Balance',
      content: () => <JournalLedgerTrialBalance />
    }
  ]

  return (
    <div>
      <SharedPageHeader
        title={t('Journal Ledger')}
        Icon={MdOutlineLibraryBooks}
        iconSize={20}
        iconClass='!text-primary'
      />
      <CustomTabs tabs={tabs} className='report-tabview' />
    </div>
  )
}

export default JournalLedgerMain
